import { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { CampaignContext } from '~/context/campaign';
import { setCampaignContribution } from '~/services/registration';
import { identityOptions } from '~/shared/config';
import {
    TextInput,
    SelectInput,
    CheckboxInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import IntlCurrencyInput from 'react-intl-currency-input';
import { Button, Col, Row, Form, FormGroup, Input, Label } from 'reactstrap';
import { Loading } from '~/shared/components/Loading';
import { fetchMyCampaign } from '../../services/campaign';
import moment from 'moment';
import { AdminContext } from '~/context/admin';
import toast from 'react-hot-toast';

const booleanOptions = [
    {
        label: 'Yes',
        value: true,
    },
    {
        label: 'No',
        value: false,
    },
];

const expensesOptions = [
    {
        label: 'Hospital Costs',
        value: 'fin_has_hospital_costs',
    },
    {
        label: 'Medications',
        value: 'fin_has_medication_costs',
    },
    {
        label: 'Travel/Temporary Relocation',
        value: 'fin_has_travel_costs',
    },
    {
        label: 'Physical Therapy/Rehabilitation',
        value: 'fin_has_phys_therapy_costs',
    },
    {
        label: 'Co-Pay/Deductibles',
        value: 'fin_has_copay_costs',
    },
    {
        label: 'Home Health Care',
        value: 'fin_has_home_healthcare_costs',
    },
    {
        label: 'Post Care',
        value: 'fin_has_post_care_costs',
    },
    {
        label: 'Living Donor Costs',
        value: 'fin_has_living_donor_costs',
    },
    {
        label: 'Donor Search',
        value: 'fin_has_donor_search',
    },
    {
        label: 'Medical Equipment/Supplies',
        value: 'fin_has_med_equip_costs',
    },
    {
        label: 'Home Modifications',
        value: 'fin_has_home_modification_costs',
    },
    {
        label: 'Accessible Transportation',
        value: 'fin_has_transportation_costs',
    },
];

export const FinancialInformation = ({ }: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const today = moment().format('Y-MM-DD');
    const {
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        register,
        formState: { errors, isSubmitting, isValid },
    } = useForm({
        defaultValues: {
            fin_num_in_household: '',
            fin_is_single_parent_household: false,
            fin_is_irs_dependent: false,
            fin_monthly_wages: 0,
            fin_soc_sec_income: 0,
            fin_disability_income: 0,
            fin_other_income: 0,
            fin_tot_monthly_income: '',
            fin_anl_household_income: '',
            fin_other_comments: '',
            fin_certify_identity: '',
            fin_signature: '',
            fin_signature_date: '',
            fin_has_hospital_costs: false,
            fin_has_medication_costs: false,
            fin_has_travel_costs: false,
            fin_has_phys_therapy_costs: false,
            fin_has_copay_costs: false,
            fin_has_home_healthcare_costs: false,
            fin_has_post_care_costs: false,
            fin_has_living_donor_costs: false,
            fin_has_donor_search: false,
            fin_has_med_equip_costs: false,
            fin_has_home_modification_costs: false,
            fin_has_transportation_costs: false,
        },
    });

    const wageIncome = watch('fin_monthly_wages') || 0;
    const socIncome = watch('fin_soc_sec_income') || 0;
    const disabilityIncome = watch('fin_disability_income') || 0;
    const otherIncome = watch('fin_other_income') || 0;
    // Round to 2 decimal places to avoid floating-point precision errors
    const monthlyIncome = Math.round((wageIncome + socIncome + disabilityIncome + otherIncome) * 100) / 100;
    const annualIncome =
        monthlyIncome > 0 ? Math.round(monthlyIncome * 12 * 100) / 100 : 0;

    const history = useHistory();
    const location = useLocation<{ read_only?: boolean }>();

    const getCampaign = async () => {
        const resp = await fetchMyCampaign();
        if (resp.detail) {
            if (resp.detail === 'Invalid token.') {
                localStorage.removeItem('token');
                history.push('/login');
            }
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    useEffect(() => {
        if (location.state) {
            const { read_only } = location.state;
            if (read_only) {
                setReadOnly(read_only);
            }
        }
    }, [location]);

    useEffect(() => {
        if (!campaign) {
            getCampaign();
        } else {
            let app = campaign.campaignApplication || {};
            app['fin_monthly_wages'] = app['fin_monthly_wages']
                ? parseFloat(app['fin_monthly_wages'])
                : 0;
            app['fin_soc_sec_income'] = app['fin_soc_sec_income']
                ? parseFloat(app['fin_soc_sec_income'])
                : 0;
            app['fin_disability_income'] = app['fin_disability_income']
                ? parseFloat(app['fin_disability_income'])
                : 0;
            app['fin_other_income'] = app['fin_other_income']
                ? parseFloat(app['fin_other_income'])
                : 0;

            const {
                fin_num_in_household,
                fin_is_single_parent_household,
                fin_is_irs_dependent,
                fin_monthly_wages,
                fin_soc_sec_income,
                fin_disability_income,
                fin_other_income,
                fin_tot_monthly_income,
                fin_anl_household_income,
                fin_other_comments,
                fin_certify_identity,
                fin_signature,
                fin_signature_date,
                fin_has_hospital_costs,
                fin_has_medication_costs,
                fin_has_travel_costs,
                fin_has_phys_therapy_costs,
                fin_has_copay_costs,
                fin_has_home_healthcare_costs,
                fin_has_post_care_costs,
                fin_has_living_donor_costs,
                fin_has_donor_search,
                fin_has_med_equip_costs,
                fin_has_home_modification_costs,
                fin_has_transportation_costs,
            } = app;

            reset({
                fin_num_in_household,
                fin_is_single_parent_household,
                fin_is_irs_dependent,
                fin_monthly_wages,
                fin_soc_sec_income,
                fin_disability_income,
                fin_other_income,
                fin_tot_monthly_income,
                fin_anl_household_income,
                fin_other_comments,
                fin_certify_identity,
                fin_signature,
                fin_signature_date,
                fin_has_hospital_costs,
                fin_has_medication_costs,
                fin_has_travel_costs,
                fin_has_phys_therapy_costs,
                fin_has_copay_costs,
                fin_has_home_healthcare_costs,
                fin_has_post_care_costs,
                fin_has_living_donor_costs,
                fin_has_donor_search,
                fin_has_med_equip_costs,
                fin_has_home_modification_costs,
                fin_has_transportation_costs,
            });

            // Reset signature date
            setValue('fin_signature_date', today);
        }
    }, [campaign]);

    useEffect(() => {
        setValue('fin_tot_monthly_income', monthlyIncome);
    }, [monthlyIncome]);

    useEffect(() => {
        setValue('fin_anl_household_income', annualIncome);
    }, [annualIncome]);

    const onSubmit = async (data) => {
        try {
            // Verify income is greater than 0
            if (monthlyIncome < 1) {
                toast.error('You must provide at least one source of income');
                return;
            }
            toast.loading('Saving financial information...');
            let payload = data;
            const resp = await setCampaignContribution(payload, campaignId);

            if (resp.status === 400) {
                toast.dismiss();
                toast.error(
                    'There was an error saving your financial information'
                );
                return;
            }

            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            toast.dismiss();
            toast.success('Financial information saved');
            history.goBack();
        } catch (e) {
            console.log('e: ', e);
            toast.dismiss();
            toast.error('There was an error saving your financial information');
        }
    };

    if (loading || !campaign) {
        return <Loading />;
    }

    return (
        <div className='container__inner'>
            <Row className='mb-5'>
                <Col lg='6'>
                    <h3>Financial Information</h3>
                    <p>
                        By collecting some of the tax information for your
                        household, we are able to better gauge what your need
                        is. A household usually includes the tax filer, their
                        spouse if they have one, and their tax dependents.
                    </p>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <TextInput
                            name='fin_num_in_household'
                            label='Number in household?'
                            type='number'
                            control={control}
                            required={true}
                            ariaRequired={true}
                            readOnly={readOnly}
                        />
                        <Label>Is this a single parent household? *</Label>
                        <FormGroup check inline className='mb-3'>
                            {booleanOptions.map((opt, i) => {
                                return (
                                    <div key={i}>
                                        <Label check>
                                            <Input
                                                type='radio'
                                                value={opt.value.toString()}
                                                onChange={(e) => {
                                                    setValue(
                                                        'fin_is_single_parent_household',
                                                        e.currentTarget
                                                            .value === 'true'
                                                    );
                                                }}
                                                name='fin_is_single_parent_household'
                                                checked={
                                                    opt.value ===
                                                    watch(
                                                        'fin_is_single_parent_household'
                                                    )
                                                }
                                                readOnly={readOnly}
                                            />
                                            {opt.label}
                                        </Label>
                                    </div>
                                );
                            })}
                        </FormGroup>
                        <Label>
                            Does a parent, spouse or other relative claim you as
                            a dependent on their tax return? *
                        </Label>
                        <FormGroup check inline className='mb-3'>
                            {booleanOptions.map((opt, i) => {
                                return (
                                    <div key={i}>
                                        <Label check>
                                            <Input
                                                type='radio'
                                                value={opt.value.toString()}
                                                onChange={(e) =>
                                                    setValue(
                                                        'fin_is_irs_dependent',
                                                        e.currentTarget
                                                            .value === 'true'
                                                    )
                                                }
                                                name='fin_is_irs_dependent'
                                                checked={
                                                    opt.value ===
                                                    watch(
                                                        'fin_is_irs_dependent'
                                                    )
                                                }
                                                readOnly={readOnly}
                                            />
                                            {opt.label}
                                        </Label>
                                    </div>
                                );
                            })}
                        </FormGroup>
                        <p>
                            Use the following to estimate your household total
                            monthly income. Household income is the combined
                            gross income of all members of a household who are
                            15 years or older, either related or non-related.
                        </p>

                        <FormGroup>
                            <Label>Monthly Salary/Wages/Tips</Label>
                            <Controller
                                name='fin_monthly_wages'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Social Security</Label>
                            <Controller
                                name='fin_soc_sec_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                            {errors.fin_soc_sec_income && (
                                <span className='text-danger'>
                                    {errors.fin_soc_sec_income.message}
                                </span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>Social Security Disability</Label>
                            <Controller
                                name='fin_disability_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                            {errors.fin_disability_income && (
                                <span className='text-danger'>
                                    {errors.fin_disability_income.message}
                                </span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Other Income (such as capital gains, dividends,
                                interest, rents, pensions, alimony)
                            </Label>
                            <Controller
                                name='fin_other_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                            {errors.fin_other_income && (
                                <span className='text-danger'>
                                    {errors.fin_other_income.message}
                                </span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Total monthly income (sum of the above)
                            </Label>
                            <IntlCurrencyInput
                                value={monthlyIncome}
                                className='form-control'
                                config={currencyConfig}
                                readOnly={true}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Annual household income (monthly income x 12)
                            </Label>
                            <IntlCurrencyInput
                                value={annualIncome}
                                className='form-control'
                                config={currencyConfig}
                                readOnly={true}
                            />
                        </FormGroup>
                        <Label className='mt-3'>
                            Please check all your anticipated out-of-pocket
                            medical and related expenses
                        </Label>
                        <FormGroup check className='mb-3'>
                            <Row>
                                {expensesOptions.map((opt, i) => {
                                    return (
                                        <Col md={6} key={i}>
                                            <CheckboxInput
                                                control={control}
                                                name={opt.value}
                                                label={opt.label}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                        </FormGroup>
                        <TextInput
                            name='fin_other_comments'
                            label='Other/additional comments'
                            type='textarea'
                            control={control}
                            readOnly={readOnly}
                        />

                        <hr />

                        <p>
                            I understand that information in this application
                            will be used by Help Hope Live to verify financial
                            need, as required by the IRS. I guarantee that the
                            above information is true to the best of my
                            knowledge.
                        </p>

                        <SelectInput
                            control={control}
                            required={true}
                            ariaRequired={true}
                            name='fin_certify_identity'
                            label='I certify that I am the:'
                            defaultLabel='Select'
                            options={identityOptions}
                            errors={errors?.fin_certify_identity?.message}
                            readOnly={readOnly}
                        />

                        <Row>
                            <Col>
                                <TextInput
                                    name='fin_signature'
                                    label='Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={readOnly}
                                />
                            </Col>
                            <Col>
                                <TextInput
                                    name='fin_signature_date'
                                    label='Date'
                                    control={control}
                                    type='date'
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={readOnly}
                                />
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Button
                    color='outline-dark'
                    disabled={isSubmitting}
                    onClick={() => history.push('/patient/edit')}
                >
                    Cancel
                </Button>
                {!readOnly && (
                    <Button
                        color='primary'
                        type='submit'
                        disabled={isSubmitting || readOnly}
                        onClick={() => {
                            handleSubmit(onSubmit)();
                        }}
                    >
                        {isSubmitting ? `Saving...` : `Save`}
                    </Button>
                )}
            </div>
        </div>
    );
};
